import axios from 'axios';
import { STOREFRONT_API_KEY, STOREFRONT_API_URL } from '../constants';
import { SubscriptionLineApiType } from '../types/api/SubscriptionApiType';
import { ChangeVariantModalViewType } from '../types/view/ChangeVariantModalViewType';

export const useMetafieldData = () => {
  /**
   * メタフィールドから取得した情報を変更可能商品情報に追加する
   *
   * @param changeVariantModal
   * @return variants
   */
  async function addMetaField(changeVariantModal: ChangeVariantModalViewType) {
    const variants = changeVariantModal.variants.map(async (variant) => {
      // variantのURLからハンドルを取得
      const startIndex = variant.url.indexOf('products/');
      const endIndex = variant.url.indexOf('?variant');
      const extractedText = variant.url.substring(startIndex, endIndex + '?variant'.length);
      const handle = extractedText.replace(/products\/|\?variant/g, '');

      // storefrontAPIからメタフィールドを取得してvariantに情報を追加
      try {
        const result = await axios.post(
          STOREFRONT_API_URL!,
          {
            query: `
              query {
                productByHandle(handle: "${handle}") {
                  subscriptionFirstPrice: metafield(namespace: "discount", key: "subscription_first_price") {
                    value
                  },
                  subscriptionSecondPrice: metafield(namespace: "discount", key: "subscription_second_price") {
                    value
                  },
                  subscriptionThirdPrice: metafield(namespace: "discount", key: "subscription_third_price") {
                    value
                  },
                  subscriptionFourthPrice: metafield(namespace: "discount", key: "subscription_fourth_price") {
                    value
                  },
                  subscriptionFifthPrice: metafield(namespace: "discount", key: "subscription_fifth_price") {
                    value
                  },
                  title: metafield(namespace: "my_account", key: "title") {
                    value
                  },
                  description: metafield(namespace: "my_account", key: "description") {
                    value
                  }
                }
              }
            `,
          },
          {
            headers: {
              'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
            },
          },
        );

        return {
          ...variant,
          subscriptionFirstPrice: result.data.data.productByHandle.subscriptionFirstPrice?.value,
          subscriptionSecondPrice: result.data.data.productByHandle.subscriptionSecondPrice?.value,
          subscriptionThirdPrice: result.data.data.productByHandle.subscriptionThirdPrice?.value,
          subscriptionFourthPrice: result.data.data.productByHandle.subscriptionFourthPrice?.value,
          subscriptionFifthPrice: result.data.data.productByHandle.subscriptionFifthPrice?.value,
          shortTitle: result.data.data.productByHandle.title?.value,
          itemDescription: result.data.data.productByHandle.description?.value,
        };
      } catch (error) {
        console.error(error);
        return {
          ...variant,
        };
      }
    });

    const processedVariants = await Promise.all(variants);

    return processedVariants;
  }

  /**
   * 必要情報を追加
   * @param variants
   * @return variants
   */
  async function addRequiredInfo(registeredVariants: SubscriptionLineApiType[]) {
    const variants = registeredVariants.map(async (variant) => {
      // variantのURLからハンドルを取得
      let handle = '';
      const startIndex = variant.onlineStorePreviewUrl.indexOf('products/');
      const endIndex = variant.onlineStorePreviewUrl.indexOf('?variant');
      if (endIndex !== -1) {
        const extractedText = variant.onlineStorePreviewUrl.substring(startIndex, endIndex + '?variant'.length);
        handle = extractedText.replace(/products\/|\?variant/g, '');
      } else {
        const extractedText = variant.onlineStorePreviewUrl.substring(startIndex);
        handle = extractedText.replace(/products\//g, '');
      }

      // variantに情報を追加
      try {
        const result = await axios.post(
          STOREFRONT_API_URL!,
          {
            query: `
              query {
                productByHandle(handle: "${handle}") {
                  subscriptionFirstPrice: metafield(namespace: "discount", key: "subscription_first_price") {
                    value
                  },
                  subscriptionSecondPrice: metafield(namespace: "discount", key: "subscription_second_price") {
                    value
                  },
                  subscriptionThirdPrice: metafield(namespace: "discount", key: "subscription_third_price") {
                    value
                  },
                  subscriptionFourthPrice: metafield(namespace: "discount", key: "subscription_fourth_price") {
                    value
                  },
                  subscriptionFifthPrice: metafield(namespace: "discount", key: "subscription_fifth_price") {
                    value
                  },
                  title: metafield(namespace: "my_account", key: "title") {
                    value
                  },
                  description: metafield(namespace: "my_account", key: "description") {
                    value
                  },
                  category: metafield(namespace: "my_account", key: "category") {
                    value
                  },
                  displayUnit: metafield(namespace: "my_account", key: "display_unit") {
                    value
                  },
                  images(first: 1) {
                    nodes {
                      url
                    }
                  },
                  variants(first: 1) {
                    nodes {
                      price {
                        amount
                      }
                    }
                  }
                }
              }
            `,
          },
          {
            headers: {
              'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
            },
          },
        );

        return {
          ...variant,
          // メタフィールド情報
          subscriptionFirstPrice: result.data.data.productByHandle?.subscriptionFirstPrice?.value ?? '',
          subscriptionSecondPrice: result.data.data.productByHandle?.subscriptionSecondPrice?.value ?? '',
          subscriptionThirdPrice: result.data.data.productByHandle?.subscriptionThirdPrice?.value ?? '',
          subscriptionFourthPrice: result.data.data.productByHandle?.subscriptionFourthPrice?.value ?? '',
          subscriptionFifthPrice: result.data.data.productByHandle?.subscriptionFifthPrice?.value ?? '',
          shortTitle: result.data.data.productByHandle?.title?.value ?? '',
          itemDescription: result.data.data.productByHandle?.description?.value ?? '',
          category: result.data.data.productByHandle?.category?.value ?? '',
          displayUnit: result.data.data.productByHandle?.displayUnit?.value ?? '',
          // 画像情報上書き（アプリ仕様で既存契約が存在する定期商品の商品画像を差し替えると画像がリンク切れするため）
          variantImage: result.data.data.productByHandle?.images.nodes[0].url ?? '',
          // 登録済みの数量を保持
          beforeChangedQuantity: variant.quantity,
          // 割引前のオリジナルプライス
          price: result.data.data.productByHandle?.variants.nodes[0].price.amount ?? '',
          // 商品ハンドル
          productHandle: handle,
        };
      } catch (error) {
        console.error(error);
        return {
          ...variant,
        };
      }
    });

    const processedVariants = await Promise.all(variants);

    return processedVariants;
  }

  /**
   * 商品情報を取得
   *
   * @param handle
   * @return variants
   */
  async function getRegistrableVariants(id: string) {
    try {
      const result = await axios.post(
        STOREFRONT_API_URL!,
        {
          query: `
            query {
              product(id: "${id}") {
                id
                title
                tags
                description
                descriptionHtml
                handle
                images(first: 10){
                  nodes {
                    url
                  }
                }
                variants(first: 1) {
                  nodes {
                    title
                    sku
                    price {
                      amount
                    }
                  }
                }
                productCapacity: metafield(namespace: "custom"
                key: "product_capacity") {
                  value
                }
                otherSizes: metafield(namespace: "custom"
                key: "product_other_sizeweight") {
                  value
                }
                nutrition: metafield(namespace: "custom"
                key: "product_nutrition") {
                  value
                }
                ingredients: metafield(namespace: "custom"
                key: "product_ingredients") {
                  value
                }
                expirationDate: metafield(namespace: "custom"
                key: "product_other_expirydate") {
                  value
                }
                howToStore: metafield(namespace: "custom"
                key: "product_other_howtostore") {
                  value
                }
                saveNote: metafield(namespace: "custom"
                key: "product_other_savenote") {
                  value
                }
                howToEat: metafield(namespace: "custom"
                key: "product_other_howtoeat") {
                  value
                }
                allergy_shrimp: metafield(namespace: "custom"
                key: "product_allergy_shrimp") {
                  value
                }
                allergy_crab: metafield(namespace: "custom"
                key: "product_allergy_crab") {
                  value
                }
                allergy_walnut: metafield(namespace: "custom"
                key: "product_allergy_walnut") {
                  value
                }
                allergy_wheat: metafield(namespace: "custom"
                key: "product_allergy_wheat") {
                  value
                }
                allergy_soba: metafield(namespace: "custom"
                key: "product_allergy_soba") {
                  value
                }
                allergy_egg: metafield(namespace: "custom"
                key: "product_allergy_egg") {
                  value
                }
                allergy_milk: metafield(namespace: "custom"
                key: "product_allergy_milk") {
                  value
                }
                allergy_peanut: metafield(namespace: "custom"
                key: "product_allergy_peanut") {
                  value
                }
                allergy_banana: metafield(namespace: "custom"
                key: "product_allergy_banana") {
                  value
                }
                allergy_beef: metafield(namespace: "custom"
                key: "product_allergy_beef") {
                  value
                }
                allergy_pork: metafield(namespace: "custom"
                key: "product_allergy_pork") {
                  value
                }
                allergy_chicken: metafield(namespace: "custom"
                key: "product_allergy_chicken") {
                  value
                }
                allergy_soy: metafield(namespace: "custom"
                key: "product_allergy_soybeans") {
                  value
                }
                allergy_orange: metafield(namespace: "custom"
                key: "product_allergy_orange") {
                  value
                }
                allergy_apple: metafield(namespace: "custom"
                key: "product_allergy_apple") {
                  value
                }
                allergy_gelatin: metafield(namespace: "custom"
                key: "product_allergy_gelatin") {
                  value
                }
                allergy_kiwi: metafield(namespace: "custom"
                key: "product_allergy_kiwi") {
                  value
                }
                allergy_sesame: metafield(namespace: "custom"
                key: "product_allergy_sesame") {
                  value
                }
                allergy_cashewnuts: metafield(namespace: "custom"
                key: "product_allergy_cashewnuts") {
                  value
                }
                allergy_matsutake: metafield(namespace: "custom"
                key: "product_allergy_matsutake") {
                  value
                }
                allergy_peach: metafield(namespace: "custom"
                key: "product_allergy_peach") {
                  value
                }
                allergy_almond: metafield(namespace: "custom"
                key: "product_allergy_almond") {
                  value
                }
                allergy_salmon: metafield(namespace: "custom"
                key: "product_allergy_salmon") {
                  value
                }
                allergy_mackerel: metafield(namespace: "custom"
                key: "product_allergy_mackerel") {
                  value
                }
                allergy_yamaimo: metafield(namespace: "custom"
                key: "product_allergy_yamaimo") {
                  value
                }
                allergy_fish: metafield(namespace: "custom"
                key: "product_allergy_fish") {
                  value
                }
                allergy_ikura: metafield(namespace: "custom"
                key: "product_allergy_ikura") {
                  value
                }
                allergy_squid: metafield(namespace: "custom"
                key: "product_allergy_squid") {
                  value
                }
                allergy_awabi: metafield(namespace: "custom"
                key: "product_allergy_awabi") {
                  value
                }
              }
              shop {
                primaryDomain {
                  url
                }
              }
            }
          `,
        },
        {
          headers: {
            'X-Shopify-Storefront-Access-Token': STOREFRONT_API_KEY,
          },
        },
      );

      return {
        // sellingPlanIdはマスターから取得
        currentPriceAmount: Math.round(result.data.data.product?.variants.nodes[0].price.amount) ?? '',
        currentPriceCurrencyCode: 'JPY',
        lineDiscountedPriceAmount: Math.round(result.data.data.product?.variants.nodes[0].price.amount) ?? '',
        lineDiscountedPriceCurrencyCode: 'JPY',
        lineId: null,
        onlineStorePreviewUrl: result.data.data.shop?.primaryDomain.url + '/products/' + result.data.data.product?.handle,
        productId: result.data.data.product?.id ?? '',
        productTitle: result.data.data.product?.title ?? '',
        quantity: 0,
        sku: result.data.data.product?.variants.nodes[0].sku ?? '',
        title: result.data.data.product?.title ?? '',
        variantId: result.data.data.product?.variants.nodes[0].id ?? '',
        featuredImage: result.data.data.product?.images.nodes[0].url ?? '',
        description: result.data.data.product?.description ?? '',
        price: Math.round(result.data.data.product?.variants.nodes[0].price.amount) ?? '',
        tags: result.data.data.product?.tags ?? '',
        images: result.data.data.product?.images.nodes.map((image: any) => image.url) ?? [],
        handle: result.data.data.product?.handle ?? '',
        descriptionHtml: result.data.data.product?.descriptionHtml ?? '',
        productHandle: result.data.data.product?.handle ?? '',
        productCapacity: result.data.data.product?.productCapacity?.value ?? '',
        otherSizes: result.data.data.product?.otherSizes?.value ?? '',
        nutrition: result.data.data.product?.nutrition?.value ?? '',
        ingredients: result.data.data.product?.ingredients?.value ?? '',
        expirationDate: result.data.data.product?.expirationDate?.value ?? '',
        howToStore: result.data.data.product?.howToStore?.value ?? '',
        saveNote: result.data.data.product?.saveNote?.value ?? '',
        howToEat: result.data.data.product?.howToEat?.value ?? '',
        allergyShrimp: result.data.data.product?.allergy_shrimp?.value ? true : false,
        allergyCrab: result.data.data.product?.allergy_crab?.value ? true : false,
        allergyWalnut: result.data.data.product?.allergy_walnut?.value ? true : false,
        allergyWheat: result.data.data.product?.allergy_wheat?.value ? true : false,
        allergySoba: result.data.data.product?.allergy_soba?.value ? true : false,
        allergyEgg: result.data.data.product?.allergy_egg?.value ? true : false,
        allergyMilk: result.data.data.product?.allergy_milk?.value ? true : false,
        allergyPeanut: result.data.data.product?.allergy_peanut?.value ? true : false,
        allergyBanana: result.data.data.product?.allergy_banana?.value ? true : false,
        allergyBeef: result.data.data.product?.allergy_beef?.value ? true : false,
        allergyPork: result.data.data.product?.allergy_pork?.value ? true : false,
        allergyChicken: result.data.data.product?.allergy_chicken?.value ? true : false,
        allergySoy: result.data.data.product?.allergy_soy?.value ? true : false,
        allergyOrange: result.data.data.product?.allergy_orange?.value ? true : false,
        allergyApple: result.data.data.product?.allergy_apple?.value ? true : false,
        allergyGelatin: result.data.data.product?.allergy_gelatin?.value ? true : false,
        allergyKiwi: result.data.data.product?.allergy_kiwi?.value ? true : false,
        allergySesame: result.data.data.product?.allergy_sesame?.value ? true : false,
        allergyCashewnuts: result.data.data.product?.allergy_cashewnuts?.value ? true : false,
        allergyMatsutake: result.data.data.product?.allergy_matsutake?.value ? true : false,
        allergyPeach: result.data.data.product?.allergy_peach?.value ? true : false,
        allergyAlmond: result.data.data.product?.allergy_almond?.value ? true : false,
        allergySalmon: result.data.data.product?.allergy_salmon?.value ? true : false,
        allergyMackerel: result.data.data.product?.allergy_mackerel?.value ? true : false,
        allergyYamaimo: result.data.data.product?.allergy_yamaimo?.value ? true : false,
        allergyFish: result.data.data.product?.allergy_fish?.value ? true : false,
        allergyIkura: result.data.data.product?.allergy_ikura?.value ? true : false,
        allergySquid: result.data.data.product?.allergy_squid?.value ? true : false,
        allergyAwabi: result.data.data.product?.allergy_awabi?.value ? true : false,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  return {
    addMetaField,
    addRequiredInfo,
    getRegistrableVariants,
  };
};
